import { Link } from '@tanstack/react-router';
import {
  Alert,
  EmptyState,
  Icon,
  IconTile,
  Page,
  PageHeader,
  cardClasses,
  cardGridClasses,
  linkTextClasses,
} from '@evogard/ui';
import { can, useSessionContext } from '../../lib/session.js';
import { hostOf, useUsefulLinks } from './usefulLinks.js';

/**
 * Links úteis — os atalhos da base para os sites que a operação usa
 * (07/10/2026).
 *
 * Todo mundo da base vê todos: a lista é `@AuthenticatedOnly()`, e o item de
 * menu não tem `recurso`. O cadastro mora na Configuração da base, sob
 * `platform.tenants:editar`, e a tela aponta para lá só para quem pode.
 *
 * Cada cartão é um `<a>` inteiro, que abre o site numa aba nova: o atalho é
 * para sair daqui, e perder a tela da plataforma no caminho seria o contrário
 * de um atalho.
 */
export function UsefulLinksScreen() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'platform.tenants', 'editar');
  const links = useUsefulLinks();

  const manage = canEdit && (
    <Link to="/tenant-settings" className={linkTextClasses()}>
      Cadastrar na Configuração da base
    </Link>
  );

  return (
    <Page measure="cheia">
      <PageHeader title="Links úteis">
        Os sites que a operação de{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>{' '}
        usa, num lugar só. Cada um abre numa aba nova.
      </PageHeader>

      {links.isError && <Alert tone="erro">{links.error.message}</Alert>}

      {links.data?.length === 0 && (
        <EmptyState
          icon="grade"
          title="Nenhum link cadastrado nesta base"
          actions={manage}
        >
          {canEdit
            ? 'Os atalhos são cadastrados na Configuração da base, com nome, endereço e ícone.'
            : 'Quem administra a Configuração da base é quem cadastra os atalhos.'}
        </EmptyState>
      )}

      {links.data && links.data.length > 0 && (
        <>
          <ul className={cardGridClasses(links.data.length)}>
            {links.data.map((link) => (
              <li key={link.id}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClasses({
                    interactive: true,
                    className:
                      'flex h-full items-start gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
                  })}
                >
                  <IconTile icon={link.icon} />
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex items-center gap-1 font-medium text-texto">
                      <span className="truncate">{link.title}</span>
                      <Icon nome="expandir" size={16} className="text-texto-suave" />
                      <span className="sr-only">(abre numa aba nova)</span>
                    </span>
                    {link.description && (
                      <span className="text-sm text-texto-suave">{link.description}</span>
                    )}
                    <span className="truncate text-xs text-texto-suave">
                      {hostOf(link.url)}
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
          {manage && <p className="text-sm">{manage}</p>}
        </>
      )}
    </Page>
  );
}
